import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import { clear as clearConfigState, fetchConfig } from "./config/config.slice";
import configService from "./services/config/config-service";
import { getEmbeddedState } from "./utils/app-bridge/app-bridge-helpers";
import {
  fetchAccount,
  isFetchAccountInProgressSelector,
  selectPreActivationAccountStatus,
} from "./account-information/account.slice";
import { useLocation, useNavigate } from "react-router-dom";
import { AccountStatusEnum } from "./account-status/account-status.types";
import { hasSessionTokenErrorSelector } from "./app.slice";

export const useAppHooks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fetchConfigAC = useRef<AbortController>();
  const fetchAccountRef = useRef<AbortController | null>(null);
  const dispatch = useAppDispatch();
  const { isEmbedded } = getEmbeddedState(
    configService.appConfig.shopifyApiKey,
  );

  const isDetachedMode = configService.appConfig.useDetached === "true";
  if (!isEmbedded && !isDetachedMode) {
    navigate("/error");
    throw new Error("Aborting...Missing apiKey or host query parameters.");
  }

  const hasSessionTokenError = useAppSelector(hasSessionTokenErrorSelector);
  useEffect(() => {
    // navigate to error page if session token fails to generate
    if (hasSessionTokenError) {
      navigate("/error", { state: { hasSessionTokenError: true } });
    }
  }, [hasSessionTokenError]);

  // allow dummy-token usage when not embedded and when useDetached config is true for detached testing/debug purpose
  const useDummyToken = !isEmbedded && isDetachedMode;

  // fetch config, account details
  useEffect(() => {
    fetchConfigAC.current?.abort();
    fetchConfigAC.current = new AbortController();
    dispatch(
      fetchConfig({
        useDummyToken,
        _signal: fetchConfigAC.current.signal,
      }),
    );

    fetchAccountRef.current = new AbortController();
    dispatch(
      fetchAccount({
        useDummyToken,
        _signal: fetchAccountRef.current.signal,
      }),
    );

    // cleanups on exiting module
    return () => {
      fetchConfigAC.current?.abort();
      fetchAccountRef.current?.abort();
      dispatch(clearConfigState());
    };
  }, []);

  const accountStatus = useAppSelector(selectPreActivationAccountStatus);
  const isFetchAccountInProgress = useAppSelector(
    isFetchAccountInProgressSelector,
  );

  useEffect(() => {
    // redirect based on Account Status
    if (
      !isFetchAccountInProgress &&
      accountStatus &&
      location.pathname !== "/error"
    ) {
      switch (accountStatus) {
        case AccountStatusEnum.NOT_SUBMITTED:
          navigate("/shipping/onboarding");
          break;

        case AccountStatusEnum.SUBMITTED:
        case AccountStatusEnum.PENDING_ACTIVATION:
        case AccountStatusEnum.PAYMENT_DETAILS_RECEIVED:
          navigate("/shipping/account-status");
          break;

        case AccountStatusEnum.ACTIVATED:
        case AccountStatusEnum.REGISTERED_CARRIER_ACCOUNT:
          navigate("/shipping/account-information");
          break;

        default:
          navigate("/error");
          break;
      }
    }
  }, [navigate, accountStatus, isFetchAccountInProgress]);
};

export const useIsAppEmbedded = () => {
  const isEmbedded = (): boolean => {
    try {
      return globalThis.top !== globalThis.self;
    } catch {
      return false;
    }
  };
  return isEmbedded;
};
